package com.fantasytenis.shared.data.lineup.dto

import kotlinx.serialization.Serializable

/**
 * Lineup slots DTO
 */
@Serializable
data class LineupSlotsDto(
    val serve: String? = null,
    val forehand: String? = null,
    val backhand: String? = null,
    val volleys: String? = null,
    val fighting: String? = null
)

/**
 * Slot costs DTO
 */
@Serializable
data class SlotCostsDto(
    val serve: Int? = null,
    val forehand: Int? = null,
    val backhand: Int? = null,
    val volleys: Int? = null,
    val fighting: Int? = null
)

/**
 * Category scores DTO
 */
@Serializable
data class CategoryScoresDto(
    val serve: Double = 0.0,
    val forehand: Double = 0.0,
    val backhand: Double = 0.0,
    val volleys: Double = 0.0,
    val fighting: Double = 0.0
)

/**
 * Individual stat contribution to a category score
 */
@Serializable
data class StatLineItemDto(
    val stat: String,
    val count: Int,
    val multiplier: Int,
    val points: Double
)

/**
 * Breakdown of how a category score was calculated
 */
@Serializable
data class CategoryBreakdownDto(
    val stats: List<StatLineItemDto> = emptyList(),
    val quickFinishBonus: Double = 0.0,
    val total: Double = 0.0
)

/**
 * Breakdowns for all 5 category slots
 */
@Serializable
data class CategoryBreakdownsDto(
    val serve: CategoryBreakdownDto? = null,
    val forehand: CategoryBreakdownDto? = null,
    val backhand: CategoryBreakdownDto? = null,
    val volleys: CategoryBreakdownDto? = null,
    val fighting: CategoryBreakdownDto? = null
)

/**
 * Lineup DTO
 */
@Serializable
data class LineupDto(
    val id: String,
    val contestEntryId: String,
    val userId: String,
    val contestId: String,
    val slots: LineupSlotsDto,
    val totalSalaryCost: Int,
    val remainingBudget: Int,
    val salaryCap: Int,
    val isValid: Boolean,
    val isLocked: Boolean,
    val validationErrors: List<String>? = null,
    val totalPoints: Double = 0.0,
    val categoryScores: CategoryScoresDto? = null,
    val categoryBreakdowns: CategoryBreakdownsDto? = null,
    val filledSlots: Int = 0
)

/**
 * Validation result DTO
 * Note: errors and warnings default to empty list to handle proto3 default value omission
 */
@Serializable
data class ValidationResultDto(
    val isValid: Boolean = false,
    val errors: List<String> = emptyList(),
    val warnings: List<String> = emptyList(),
    val totalCost: Int = 0,
    val remainingBudget: Int = 0,
    val slotCosts: SlotCostsDto = SlotCostsDto()
)

/**
 * Create lineup request
 */
@Serializable
data class CreateLineupRequest(
    val contestEntryId: String,
    val contestId: String,
    val slots: LineupSlotsDto? = null
)

/**
 * Update lineup request
 */
@Serializable
data class UpdateLineupRequest(
    val slots: LineupSlotsDto
)

/**
 * Validate lineup request
 */
@Serializable
data class ValidateLineupRequest(
    val contestId: String,
    val slots: LineupSlotsDto
)

/**
 * Can add player request
 */
@Serializable
data class CanAddPlayerRequest(
    val category: String,
    val playerId: String
)

/**
 * Can add player response data
 */
@Serializable
data class CanAddPlayerResponseData(
    val canAdd: Boolean,
    val newTotal: Int,
    val reason: String? = null
)

/**
 * Lineup response with validation
 */
@Serializable
data class LineupWithValidationDto(
    val data: LineupDto?,
    val validation: ValidationResultDto?
)
